import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { UserVideoWord, UserWordStatus } from "@shared_types";
import { isInvalidDictionaryWord } from "./invalidDictionaryWord.ts";
import { haptic } from "@/lib/haptic.ts";
interface WordHighlighterProps {
  word: UserVideoWord;
  // so each word knows about its neighbors
  allWords: UserVideoWord[];
  // data about the words position in the sentence.
  position: number;
  // special word styling options
  highlightPosition: number;
  isRightMostPhraseWord: boolean;
  isLeftMostPhraseWord: boolean;
  isPhraseMiddle: boolean;
  // phrase should be left highlighted, even if not dragging
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick: (word: UserVideoWord) => void;
  onPressDown: (word: UserVideoWord) => void;
  onPressMove: (word: UserVideoWord) => void;
  onPressUp: () => void;
}

const WordHighlighter: React.FC<WordHighlighterProps> = ({
  word,
  allWords,
  position,
  highlightPosition,
  onWordClick,
  onDoubleWordClick,
  isRightMostPhraseWord = false,
  isLeftMostPhraseWord = false,
  isPhraseMiddle = false,
  onPressDown = () => {},
  onPressMove = () => {},
  onPressUp = () => {},
}) => {
  const highlightColorBlue = "bg-[var(--color-blue)]";
  const learningColorLightYellow = "bg-[var(--color-light-yellow)]";
  const newWordColor = "bg-[var(--color-yellow)]";
  const [lastTap, setLastTap] = useState(0);

  const wordCls = (status: UserWordStatus) => {
    if (isInvalidDictionaryWord(word.displayText)) {
      return "bg-transparent";
    }
    switch (status) {
      case "known":
        return "bg-transparent";
      case "learning":
        return learningColorLightYellow;
      case "new":
        return newWordColor;
      default:
        return "bg-transparent";
    }
  };

  const handleClick = () => {
    // Only handle click if we're not in drag mode
    const currentTime = new Date().getTime();
    const tapGap = currentTime - lastTap;
    if (tapGap < 300 && tapGap > 0 && onDoubleWordClick) {
      haptic.light();
      onDoubleWordClick(word);
    } else {
      onWordClick(word);
    }
    setLastTap(currentTime);
  };

  // Determine the highlighting class based on the context
  let innerWordHighlightColor = "";

  if (position === highlightPosition) {
    innerWordHighlightColor = highlightColorBlue + " text-white";
  } else {
    innerWordHighlightColor = wordCls(word.status);
  }

  let spanColor = "bg-transparent";
  let spanRound = "rounded-md";

  if (isLeftMostPhraseWord || isRightMostPhraseWord || isPhraseMiddle) {
    spanColor = highlightColorBlue;
    innerWordHighlightColor = highlightColorBlue + " text-white";
  }

  if (isPhraseMiddle) {
    //no round
    spanRound = "";
  } else if (isLeftMostPhraseWord) {
    spanRound = "rounded-l-md";
  } else if (isRightMostPhraseWord) {
    spanRound = "rounded-r-md";
  }

  // Handle isDragging state (different from isDraggingPhrase)

  return (
    <span
      className={`cursor-pointer ${spanColor + " " + spanRound}  
      selection:text-white inline-flex 
      items-center justify-center px-[2px] my-1`}
      key={word.videoWordId}
      onMouseDown={(e) => {
        e.preventDefault(); // Prevent default text selection
        onPressDown(word);
      }}
      onMouseEnter={() => {
        onPressMove(word);
      }}
      onTouchStart={() => {
        // Don't prevent default here to allow for normal taps
        onPressDown(word);
      }}
      onTouchMove={(event: React.TouchEvent<HTMLSpanElement>) => {
        // Remove the preventDefault call since we're using CSS to handle this
        const dataPos = getPosFromTouchEvent(event);
        if (dataPos) {
          const wordUnderTouch = allWords[parseInt(dataPos)];
          onPressMove(wordUnderTouch);
        }
      }}
      onTouchEnd={() => {
        // Let the handler decide whether to prevent default
        onPressUp();
      }}
    >
      <span
        data-pos={position}
        className={innerWordHighlightColor + " rounded-md px-[3px]"}
        onClick={handleClick}
      >
        {word.displayText}
      </span>{" "}
    </span>
  );
};

function getPosFromTouchEvent(event: React.TouchEvent<HTMLSpanElement>) {
  const touch = event.changedTouches[0];
  const x = touch.clientX;
  const y = touch.clientY;
  const elementUnderTouch = document.elementFromPoint(x, y);
  return elementUnderTouch?.getAttribute("data-pos");
}

interface HighlightedWordsProps {
  words: UserVideoWord[];
  highlightPosition: number;
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick?: (word: UserVideoWord) => void;
  reviewWord?: UserVideoWord;
  onPhraseSelectedDone: (
    startWord: UserVideoWord,
    endWord: UserVideoWord
  ) => void;
  onPhraseSelectStart: () => void;
}

// Define the ref type with the clearPhraseSelection method
export interface HighlightedWordsRef {
  clearPhraseSelection: () => void;
  getParagraphRef: () => HTMLParagraphElement | null;
}

export const HighlightedWords = forwardRef<
  HighlightedWordsRef,
  HighlightedWordsProps
>(
  (
    {
      words,
      highlightPosition,
      onWordClick,
      onDoubleWordClick,
      onPhraseSelectedDone,
      onPhraseSelectStart,
    },
    ref
  ) => {
    // the real indexes of the real current final selection
    // remember these may go either left or right of the words.
    const [isDraggingPhrase, setIsDraggingPhrase] = useState(false);
    const [phraseStartIndex, setPhraseStartIndex] = useState<number | null>(
      null
    );
    const [phraseEndIndex, setPhraseEndIndex] = useState<number | null>(null);

    const paragraphRef = useRef<HTMLParagraphElement>(null);
    // Expose the clearPhraseSelection method to parent components via ref
    useImperativeHandle(ref, () => ({
      clearPhraseSelection: () => {
        setPhraseStartIndex(null);
        setPhraseEndIndex(null);
        setIsDraggingPhrase(false);
      },
      getParagraphRef: () => paragraphRef.current,
    }));

    const downHapticOnce = useRef(false);
    // Handle mouse/touch events for phrase selection
    const handlePressDown = (word: UserVideoWord) => {
      const wordIndex = words.findIndex(
        (w) => w.videoWordId === word.videoWordId
      );
      if (!downHapticOnce.current) {
        haptic.selection();
        downHapticOnce.current = true;
        setTimeout(() => {
          downHapticOnce.current = false;
        }, 500);
      }
      setPhraseStartIndex(wordIndex);
      setPhraseEndIndex(wordIndex);
      setIsDraggingPhrase(true);
      onPhraseSelectStart();
      onWordClick(word);
    };

    const handlePressMove = (word: UserVideoWord) => {
      const wordIndex = words.findIndex(
        (w) => w.videoWordId === word.videoWordId
      );
      if (isDraggingPhrase) {
        setPhraseEndIndex(wordIndex);
      }
    };

    // Attach/detach mouseup in an effect so it sees *current* state.
    // this catches mouse ups if the event happnes off the word children
    useEffect(() => {
      function handleGlobalMouseUp() {
        handlePressUp();
      }

      if (isDraggingPhrase) {
        document.addEventListener("mouseup", handleGlobalMouseUp);
      }
      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }, [
      isDraggingPhrase,
      phraseStartIndex,
      phraseEndIndex,
      words,
      onPhraseSelectedDone,
    ]);

    const upHapticOnce = useRef(false);
    // user finished dragging, so we will take the last word they were over
    const handlePressUp = () => {
      if (
        isDraggingPhrase &&
        phraseStartIndex !== null &&
        phraseEndIndex !== null
      ) {
        // we are done dragging
        setIsDraggingPhrase(false);
        if (!upHapticOnce.current) {
          haptic.selection();
          upHapticOnce.current = true;
          setTimeout(() => {
            upHapticOnce.current = false;
          }, 500);
        }
        const startWord = words[phraseStartIndex];
        const endWord = words[phraseEndIndex];
        onPhraseSelectedDone(startWord, endWord);
      }
    };

    return (
      <p
        ref={paragraphRef}
        style={{
          lineHeight: "1.3",
        }}
      >
        {words.map((word, index) => {
          let isRightMostPhraseWord = false;
          let isLeftMostPhraseWord = false;
          let isPhraseMiddle = false;
          const isLengthOne = phraseStartIndex == phraseEndIndex;
          if (
            phraseStartIndex !== null &&
            phraseEndIndex !== null &&
            !isLengthOne
          ) {
            const leftMostIdx = Math.min(phraseStartIndex, phraseEndIndex);
            const rightMostIdx = Math.max(phraseStartIndex, phraseEndIndex);
            isRightMostPhraseWord = rightMostIdx === index;
            isLeftMostPhraseWord = leftMostIdx === index;
            isPhraseMiddle = leftMostIdx < index && index < rightMostIdx;
          }
          return (
            <WordHighlighter
              key={word.videoWordId}
              word={word}
              allWords={words}
              position={index}
              highlightPosition={highlightPosition}
              onWordClick={onWordClick}
              onDoubleWordClick={onDoubleWordClick || (() => {})}
              isRightMostPhraseWord={isRightMostPhraseWord}
              isLeftMostPhraseWord={isLeftMostPhraseWord}
              isPhraseMiddle={isPhraseMiddle}
              onPressDown={handlePressDown}
              onPressMove={handlePressMove}
              onPressUp={handlePressUp}
            />
          );
        })}
      </p>
    );
  }
);

export default HighlightedWords;
